import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Bell,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Dumbbell,
  GraduationCap,
  LayoutDashboard,
  MapPin,
  Scale,
  Shield,
  User,
  type LucideIcon,
} from "lucide-react";

import { useAuth } from "@/auth";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import LogoutButton from "@/components/LogoutButton";
import NotificationPanel from "@/components/notifications/NotificationPanel";
import { useAdmin } from "@/components/admin/AdminGate";
import { useEntitlement } from "@/entitlement/EntitlementGateContext";
import useFirstPassUnlocked from "@/hooks/useFirstPassUnlocked";
import { resolveAdminUrl } from "@/lib/admin-url";

/**
 * LearnerPortalShell — white-sidebar layout for the authenticated learner
 * surface (`/dashboard`, `/practice`, `/settings`, `/training`, etc.).
 *
 * Adopted from the 2026-05-04 Replit graduation under a stripped scope
 * (RB-G6, PR #299), then expanded to all auth pages and given a
 * collapsible-sidebar toggle in RB-G8 (this PR).
 *
 * Modifications vs the quarantine version:
 *   1. Outer wrapper is a `<div>` rather than `<main>` so it doesn't
 *      collide with App.tsx's a11y skip-target `<main id="main-content">`.
 *      Two `<main>` elements per document is invalid HTML.
 *   2. All hardcoded English replaced with `learner.portal.*` i18n keys.
 *   3. The `<Bell>` notification button is retained as a placeholder
 *      with an i18n'd `aria-label` and a TODO. A future ticket can wire
 *      up a real notifications panel.
 *   4. Collapsible sidebar (RB-G8): chevron toggle in the header bar
 *      flips between full (`w-60`) and icons-only (`w-14`); state
 *      persisted in `localStorage` under `rb:learner-sidebar-collapsed:v1`.
 *      Icons-only mode hides text labels, keeps icons + tooltips.
 *      Toggle is `md:flex`-gated to match the sidebar's own visibility
 *      (mobile drawer is a separate ticket).
 */
interface LearnerPortalShellProps {
  /** Page title shown in the desktop top header bar. */
  pageTitle: string;
  /** Optional right-hand slot in the header (e.g. state badge / state switcher). */
  headerRight?: ReactNode;
  children: ReactNode;
}

interface NavItem {
  to: string;
  /** i18n key under `learner.portal.nav` (e.g. "dashboard"). */
  i18nKey: string;
  Icon: LucideIcon;
  /** When true, NavLink uses `end` matching (root-of-section). */
  exact?: boolean;
}

const NAV_ITEMS: readonly NavItem[] = [
  { to: "/dashboard", i18nKey: "dashboard", Icon: LayoutDashboard, exact: true },
  { to: "/practice", i18nKey: "practice", Icon: Dumbbell },
  { to: "/training", i18nKey: "training", Icon: GraduationCap },
  { to: "/exam/history", i18nKey: "exams", Icon: ClipboardList },
  { to: "/rule-diff/browse", i18nKey: "compareRules", Icon: Scale },
  { to: "/profile", i18nKey: "profile", Icon: User },
];

/**
 * The two routes the learner can ALWAYS reach. When entitlements lapse,
 * every other nav item is hidden so the user is funnelled to the
 * conversion surfaces. Anchored to the route path (not the i18nKey) so
 * future renames don't silently disable the gate.
 */
const ALWAYS_REACHABLE_PATHS: ReadonlySet<string> = new Set([
  "/dashboard",
  "/profile",
]);

/**
 * The post-pass nav item lives outside `NAV_ITEMS` because it is
 * conditionally rendered after a first-pass gate (ROADS-431). It is
 * positioned immediately after "Exams" / `/exam/history` per the
 * journey-arc specification: the user just passed an exam → the next
 * thing they want is the roadmap.
 */
const WHATS_NEXT_NAV_ITEM: NavItem = {
  to: "/whats-next",
  i18nKey: "whatsNext",
  Icon: MapPin,
};
const WHATS_NEXT_INSERT_AFTER: string = "/exam/history";

const COLLAPSED_STORAGE_KEY = "rb:learner-sidebar-collapsed:v1";

function readCollapsedFromStorage(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeCollapsedToStorage(value: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(COLLAPSED_STORAGE_KEY, String(value));
  } catch {
    // localStorage may be unavailable (private browsing, quota) — UI
    // still toggles for the session, just doesn't persist.
  }
}

/**
 * Administration nav entry. Conditional render is the caller's job;
 * this component handles the absolute-vs-relative URL decision via
 * `resolveAdminUrl`. When `VITE_ADMIN_URL` is unset (dev / unified
 * hostname mode), we render a `<NavLink>` for same-origin SPA routing.
 * When it's set to an `http(s)://` origin (Phase 1A of ADR-0013 — admin
 * isolated on a separate subdomain), we render a plain `<a>` so the
 * navigation does a full page load to the admin origin and the admin
 * SPA boots fresh under that bundle.
 */
function AdminLink({ isCollapsed }: { isCollapsed: boolean }) {
  const envValue = (import.meta.env as Record<string, string | undefined>)
    .VITE_ADMIN_URL;
  const { href, external } = resolveAdminUrl("/admin/users", envValue);

  const innerClasses = `flex items-center rounded-xl text-sm font-medium transition-colors no-underline ${
    isCollapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5"
  } text-red-700 hover:bg-red-50`;

  const inner = (
    <>
      <Shield aria-hidden="true" size={17} className="text-red-600" />
      {!isCollapsed && "Administration"}
    </>
  );

  return (
    <>
      <hr
        aria-hidden="true"
        className={`my-2 border-slate-100 ${isCollapsed ? "mx-1" : "mx-2"}`}
      />
      {external ? (
        <a
          href={href}
          title={isCollapsed ? "Administration" : undefined}
          aria-label={isCollapsed ? "Administration" : undefined}
          data-testid="learner-portal-admin-link"
          data-external="true"
          className={innerClasses}
        >
          {inner}
        </a>
      ) : (
        <NavLink
          to={href}
          title={isCollapsed ? "Administration" : undefined}
          aria-label={isCollapsed ? "Administration" : undefined}
          data-testid="learner-portal-admin-link"
          className={({ isActive }) =>
            `${innerClasses} ${isActive ? "bg-red-50 font-semibold" : ""}`
          }
        >
          {inner}
        </NavLink>
      )}
    </>
  );
}

export default function LearnerPortalShell({
  children,
  pageTitle,
  headerRight,
}: LearnerPortalShellProps) {
  const { t, i18n } = useTranslation();
  const { state } = useAuth();
  const user = state.kind === "authenticated" ? state.user : null;
  const fallbackName = t("learner.portal.userFallback");
  const displayName =
    user?.first_name?.trim() ||
    user?.email?.split("@")[0] ||
    fallbackName;
  const initial = (
    user?.first_name?.[0] ||
    user?.email?.[0] ||
    fallbackName[0] ||
    "L"
  ).toUpperCase();

  const firstPass = useFirstPassUnlocked();
  const { isExpired } = useEntitlement();
  const { isAdmin } = useAdmin();

  // Conditional "What's next" insertion after `/exam/history` once the
  // learner has at least one passed exam attempt (ROADS-431). When the
  // gate is still loading we leave the item out — flicker-on-mount is
  // worse for perceived performance than a slightly delayed reveal.
  //
  // When `isExpired`, all nav items remain visible so users can see what
  // they're missing. Locked items are rendered grayed-out and non-clickable
  // in the render layer (below) — `<RequiresEntitlement>` still redirects
  // if users navigate directly via URL.
  const visibleNavItems: readonly NavItem[] = (() => {
    if (!firstPass.unlocked || isExpired) return NAV_ITEMS;
    const idx = NAV_ITEMS.findIndex(
      (item) => item.to === WHATS_NEXT_INSERT_AFTER,
    );
    if (idx < 0) return [...NAV_ITEMS, WHATS_NEXT_NAV_ITEM];
    return [
      ...NAV_ITEMS.slice(0, idx + 1),
      WHATS_NEXT_NAV_ITEM,
      ...NAV_ITEMS.slice(idx + 1),
    ];
  })();

  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [notifOpen, setNotifOpen] = useState(false);
  // Read persisted collapsed state in an effect so SSR (where `window` is
  // undefined) renders the default expanded state and hydration doesn't
  // diverge from server output.
  useEffect(() => {
    setIsCollapsed(readCollapsedFromStorage());
  }, []);
  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      writeCollapsedToStorage(next);
      return next;
    });
  }, []);

  const toggleLabel = isCollapsed
    ? t("learner.portal.expandSidebar")
    : t("learner.portal.collapseSidebar");

  return (
    <div className="flex h-screen bg-slate-50 font-sans text-slate-900 overflow-hidden">
      {/* ── LEFT SIDEBAR ──────────────────────────────────────────── */}
      <aside
        aria-label={t("learner.portal.navLabel")}
        data-collapsed={isCollapsed}
        className={`hidden md:flex shrink-0 flex-col bg-white border-r border-slate-200 transition-[width] duration-200 ${
          isCollapsed ? "w-14" : "w-60"
        }`}
      >
        {/* Brand */}
        <div
          className={`flex items-center border-b border-slate-100 ${
            isCollapsed ? "justify-center px-0 py-5" : "gap-2.5 px-5 py-5"
          }`}
        >
          <Link
            to="/dashboard"
            className="flex items-center gap-2.5 no-underline"
            aria-label="RoadBridge"
          >
            <img
              src="/favicon.png"
              alt=""
              aria-hidden="true"
              className="w-8 h-8 rounded-lg shrink-0"
            />
            {!isCollapsed && (
              <div>
                <p className="font-black text-base text-slate-900 leading-none">
                  RoadBridge
                </p>
                {/* GH #326: was `text-slate-400` (2.56:1 on white) — now
                    `text-neutral-600` (#475569 on white = 7.58:1, AA pass). */}
                <p className="text-[10px] text-neutral-600 font-medium mt-0.5">
                  {t("learner.portal.brandTagline")}
                </p>
              </div>
            )}
          </Link>
        </div>

        {/* Nav */}
        <nav
          className={`flex-1 py-4 space-y-0.5 ${
            isCollapsed ? "px-2" : "px-3"
          }`}
        >
          {visibleNavItems.map(({ to, i18nKey, Icon, exact }) => {
            const isLocked = isExpired && !ALWAYS_REACHABLE_PATHS.has(to);
            if (isLocked) {
              return (
                <span
                  key={to}
                  aria-disabled="true"
                  title={isCollapsed ? t(`learner.portal.nav.${i18nKey}`) : undefined}
                  className={`flex items-center rounded-xl text-sm font-medium cursor-not-allowed select-none opacity-35 ${
                    isCollapsed
                      ? "justify-center px-2 py-2.5"
                      : "gap-3 px-3 py-2.5"
                  } text-slate-500`}
                >
                  <Icon aria-hidden="true" size={17} className="text-slate-400" />
                  {!isCollapsed && t(`learner.portal.nav.${i18nKey}`)}
                </span>
              );
            }
            return (
              <NavLink
                key={to}
                to={to}
                end={exact}
                title={isCollapsed ? t(`learner.portal.nav.${i18nKey}`) : undefined}
                aria-label={isCollapsed ? t(`learner.portal.nav.${i18nKey}`) : undefined}
                className={({ isActive }) =>
                  `flex items-center rounded-xl text-sm font-medium transition-colors no-underline ${
                    isCollapsed
                      ? "justify-center px-2 py-2.5"
                      : "gap-3 px-3 py-2.5"
                  } ${
                    isActive
                      ? "bg-teal-50 text-teal-700 font-semibold"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      aria-hidden="true"
                      size={17}
                      className={isActive ? "text-teal-600" : "text-slate-400"}
                    />
                    {!isCollapsed && t(`learner.portal.nav.${i18nKey}`)}
                  </>
                )}
              </NavLink>
            );
          })}
          {/*
            Administration entry — only rendered when `useAdmin()` resolves
            to true, which means the AdminGate probe returned 200. Visually
            distinct (red accent) to signal "you are leaving the learner
            surface and entering an ops console with destructive actions".
            Lives at the bottom of the nav, after a thin divider, so it
            never competes with the learner journey for primary attention.
          */}
          {isAdmin && (
            <AdminLink isCollapsed={isCollapsed} />
          )}
        </nav>

        {/* User footer */}
        <div
          className={`border-t border-slate-100 ${
            isCollapsed ? "px-2 py-3" : "px-4 py-4"
          }`}
        >
          <div
            className={`flex items-center ${
              isCollapsed ? "justify-center" : "gap-3 mb-3"
            }`}
            title={isCollapsed ? `${displayName} — ${user?.email ?? ""}`.trim() : undefined}
          >
            <div
              aria-hidden="true"
              className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center text-sm font-bold text-teal-700 shrink-0"
            >
              {initial}
            </div>
            {!isCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">
                  {displayName}
                </p>
                {/* GH #326: was `text-slate-400` (2.56:1 on white) — now
                    `text-neutral-600` (#475569 on white = 7.58:1, AA pass). */}
                <p className="text-xs text-neutral-600 truncate">
                  {user?.email ?? ""}
                </p>
              </div>
            )}
          </div>
          {!isCollapsed && (
            // Tokens: `text-neutral-600` (#475569) on `bg-white` = 7.58:1 —
            // clears WCAG 2.2 AA's 4.5:1 normal-text floor with margin.
            // Hover swaps to `text-neutral-700` on `bg-neutral-100` = 9.45:1.
            // Previously shipped `text-slate-400` on white = 2.56:1 (GH #326).
            // Uses the project's named neutrals (mirrors Tailwind `slate.*`
            // by D14, see packages/design-tokens/tokens.json) — never
            // arbitrary `text-[#XYZ]` (durable feedback: flaky on Webkit).
            <LogoutButton className="flex items-center gap-2 text-xs font-medium text-neutral-600 hover:text-neutral-700 transition-colors px-1 py-1 rounded hover:bg-neutral-100 w-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-700" />
          )}
        </div>
      </aside>

      {/* ── RIGHT: HEADER + CONTENT ───────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="shrink-0 h-14 bg-white border-b border-slate-200 flex items-center justify-between px-5 sticky top-0 z-10">
          <div className="flex items-center gap-2 min-w-0">
            {/* Sidebar collapse toggle — desktop only (matches sidebar visibility) */}
            <button
              type="button"
              onClick={toggleCollapsed}
              aria-label={toggleLabel}
              aria-expanded={!isCollapsed}
              className="hidden md:inline-flex items-center justify-center p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-700"
            >
              {isCollapsed ? (
                <ChevronRight aria-hidden="true" size={18} />
              ) : (
                <ChevronLeft aria-hidden="true" size={18} />
              )}
            </button>
            {/* Mobile-only brand chip (sidebar is hidden below md:) */}
            <div className="flex items-center gap-2 md:hidden">
              <img
                src="/favicon.png"
                alt=""
                aria-hidden="true"
                className="w-7 h-7 rounded-lg"
              />
              <span className="font-black text-base text-slate-900">
                RoadBridge
              </span>
            </div>
            {/* Desktop title */}
            <h1 className="hidden md:block text-base font-semibold text-slate-800 truncate">
              {pageTitle}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            {headerRight}
            <LanguageSwitcher
              currentLang={i18n.language}
              onChange={(l) => void i18n.changeLanguage(l)}
              variant="compact"
            />
            <div className="relative">
              <button
                type="button"
                aria-label={t("learner.portal.notifications")}
                aria-expanded={notifOpen}
                aria-controls="notification-panel"
                onClick={() => setNotifOpen((o) => !o)}
                className="relative p-2 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-700"
              >
                <Bell aria-hidden="true" size={18} />
              </button>
              <div id="notification-panel">
                <NotificationPanel
                  isOpen={notifOpen}
                  onClose={() => setNotifOpen(false)}
                />
              </div>
            </div>
            {/* Mobile-only avatar (sidebar is hidden below md:) */}
            <div
              aria-hidden="true"
              className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-sm font-bold text-teal-700 md:hidden"
            >
              {initial}
            </div>
          </div>
        </header>

        {/* Scrollable content. NB: not <main> — App.tsx already wraps the
            entire route tree in <main id="main-content"> for the a11y
            skip-link target. Two <main>s per document is invalid HTML. */}
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
